import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { analytics } from '../services/analytics';
import SEO from '../components/SEO';

// Icons
const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ZapIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const BarChartIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="20" x2="12" y2="10" />
    <line x1="18" y1="20" x2="18" y2="4" />
    <line x1="6" y1="20" x2="6" y2="16" />
  </svg>
);

const XIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const HVACQuotingSoftware = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Structured data for HVAC page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product", 
    "name": "HVAC Quoting Software by QuoteTier",
    "description": "Interactive HVAC quoting software that lets customers choose their own upgrades. Increase average job size with Good/Better/Best quotes.",
    "category": "Software",
    "brand": {
      "@type": "Brand",
      "name": "QuoteTier"
    },
    "offers": {
      "@type": "Offer",
      "name": "Free Forever",
      "price": "0",
      "priceCurrency": "USD"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": "HVAC Contractors"
    }
  };

  useEffect(() => {
    analytics.track('HVAC Landing Page View');
  }, []);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        // Scrolling up or near top - show header
        setIsVisible(true);
      } else {
        // Scrolling down - hide header
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    return () => window.removeEventListener('scroll', controlHeader);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="HVAC Quoting Software — Close Bigger Jobs with Good/Better/Best Quotes | QuoteTier"
        description="HVAC contractors using tiered quotes close 30-40% bigger jobs. Show monthly payments, rebates, and SEER2 specs in Good/Better/Best proposals your customers can sign on the spot. Free to try."
        canonical="/hvac-quoting-software"
        structuredData={structuredData}
        ogTitle="HVAC Quoting Software That Increases Deal Size"
        ogDescription="HVAC quotes with monthly payments, rebates, and AHRI specs shown clearly. Let customers self-select upgrades and choose higher-value options with confidence."
      />
      {/* Header */}
      <header 
        className={`fixed top-4 left-1/2 z-50 w-[90%] max-w-4xl -translate-x-1/2 rounded-2xl border border-black/10 bg-white/95 shadow-lg transition-all duration-300 ease-in-out ${
          isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center">
            <ZapIcon className="h-4 w-4 mr-2 text-primary" />
            <span className="font-instrument text-xl md:text-2xl">QuoteTier</span>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
            <Link to="/blog/" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'navigation', action: 'signup' })}>
              <Button className="font-instrument text-xl px-4 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Start Free
              </Button>
            </Link>
          </nav>
          
          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center space-x-2">
            <Link to="/blog/" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'mobile-navigation', action: 'signup' })}>
              <Button className="font-instrument text-sm px-3 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-32 md:py-40 lg:py-48">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
                HVAC Quoting Software That <span className="text-primary font-bold">Increases Your Job Size</span>
              </h1>
              <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
                Built for HVAC contractors: let customers self‑select upgrades, see “From $/mo” pricing, and understand rebates and efficiency in one clean quote.
              </p>
              <div className="font-instrument mt-12 flex flex-col sm:flex-row gap-6 justify-center items-center mb-6">
                <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'hvac-hero', action: 'signup' })}>
                  <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                    Start Free — 3 Quotes/Mo
                  </Button>
                </Link>
                <Link to="/demo-quote/hvac" onClick={() => analytics.track('Demo Clicked', { location: 'hvac-hero', industry: 'hvac' })}>
                  <Button className="px-8 py-4 text-xl font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg" variant="outline">
                    See HVAC Quote Demo →
                  </Button>
                </Link>
              </div>
              <p className="font-jetbrains mt-10 text-lg text-gray-500">
                Free forever • No credit card required • Setup in 5 minutes
              </p>
            </div>
          </div>
        </section>

        {/* HVAC Value Props */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Why HVAC Owners Choose QuoteTier</h2>
              <p className="font-jetbrains text-lg text-gray-600">Purpose‑built features that match real HVAC workflows</p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Monthly payments that sell</CardTitle>
                  <CardDescription className="font-jetbrains">Show “From $X/mo” with clear APR, term, and down payment assumptions.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />No lender integration required</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Works across all trades and packages</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Rebates & incentives</CardTitle>
                  <CardDescription className="font-jetbrains">Add utility/manufacturer rebates (flat $ or %) and show estimated savings.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Notes and disclaimers included in‑quote</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Clear “Estimated incentives” line in summary</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">AHRI & efficiency details</CardTitle>
                  <CardDescription className="font-jetbrains">Display SEER2, HSPF2, AFUE and model numbers with AHRI reference link.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Outdoor/indoor/coil/furnace models per package</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Inspector‑friendly AHRI certificate URL</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Scope & allowances</CardTitle>
                  <CardDescription className="font-jetbrains">Quantity‑based adders for Line Set, Electrical, Permits, Crane, Ductwork, Thermostat, and more.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Min/max, defaults, per‑tier availability</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Pre‑selects for Better/Best to guide choices</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What Clients See */}
        <section className="py-16 md:py-24">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">What Your Client Sees</h2>
              <p className="font-jetbrains text-lg text-gray-600">Clarity that speeds decisions and lifts average tickets</p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border border-black"><CardHeader><CardTitle className="font-instrument text-xl">Clear packages</CardTitle><CardDescription className="font-jetbrains">Three options with total price and “From $X/mo” underneath.</CardDescription></CardHeader></Card>
              <Card className="border border-black"><CardHeader><CardTitle className="font-instrument text-xl">Savings summary</CardTitle><CardDescription className="font-jetbrains">“Estimated incentives” with your notes and links to details.</CardDescription></CardHeader></Card>
              <Card className="border border-black"><CardHeader><CardTitle className="font-instrument text-xl">Equipment & ratings</CardTitle><CardDescription className="font-jetbrains">SEER2/HSPF2/AFUE, model numbers, and AHRI certificate link.</CardDescription></CardHeader></Card>
              <Card className="border border-black"><CardHeader><CardTitle className="font-instrument text-xl">Scope clarity</CardTitle><CardDescription className="font-jetbrains">Allowances like line set length, duct transitions, crane day with quantity and price.</CardDescription></CardHeader></Card>
            </div>
          </div>
        </section>

        {/* Built for Real Jobs */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Built for Real HVAC Jobs</h2>
              <p className="font-jetbrains text-lg text-gray-600">From standard replacements to complex scopes</p>
            </div>
            <ul className="grid gap-3 md:grid-cols-2 font-jetbrains text-base text-gray-800 max-w-4xl mx-auto">
              <li>• Split systems, heat pumps, gas furnaces, coils, mini‑splits, packaged units</li>
              <li>• Line set increments, disconnect/whip, float switch, condensate pump</li>
              <li>• Low‑voltage rewires, permit fees, curb adapter, crane day</li>
              <li>• Smart thermostats: keep‑as‑is in Standard, pre‑select in Better/Best</li>
              <li>• Local utility/manufacturer incentives with disclaimers</li>
            </ul>
          </div>
        </section>

        {/* Setup Steps */}
        <section className="py-16 md:py-24">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Set Up in Minutes</h2>
              <p className="font-jetbrains text-lg text-gray-600">No heavy configuration—just the details that matter</p>
            </div>
            <ol className="max-w-3xl mx-auto list-decimal list-inside space-y-3 font-jetbrains text-gray-800">
              <li>Create Good/Better/Best with price and scope.</li>
              <li>Add equipment metadata and AHRI reference/certificate URL.</li>
              <li>Toggle monthly payments and set APR/term/down payment defaults.</li>
              <li>Add rebates (flat $ or %) with notes and disclaimer text.</li>
              <li>Add scope/allowances with quantities, min/max, and per‑tier visibility or pre‑selects.</li>
            </ol>
          </div>
        </section>

        {/* Compliance & Trust */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Compliance, Clarity, and Trust</h2>
            </div>
            <ul className="max-w-3xl mx-auto space-y-2 font-jetbrains text-gray-800">
              <li>• AHRI certificate link keeps inspectors, homeowners, and your team aligned</li>
              <li>• Monthly payment clearly labeled as an estimate with your terms</li>
              <li>• Rebates shown as “estimated” with your conditions and documentation</li>
              <li>• Mobile‑optimized presentation looks great on‑site and on desktop</li>
            </ul>
          </div>
        </section>

        {/* Results */}
        <section className="py-16 md:py-24">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Results HVAC Pros Care About</h2>
            </div>
            <ul className="max-w-3xl mx-auto space-y-2 font-jetbrains text-gray-800">
              <li>• Bigger average tickets as clients choose Better/Best</li>
              <li>• Fewer callbacks with scope and allowance clarity</li>
              <li>• Faster approvals from a confident, interactive decision</li>
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">HVAC FAQs</h2>
            </div>
            <div className="max-w-3xl mx-auto space-y-6 font-jetbrains text-gray-800">
              <div>
                <p className="font-semibold">How do monthly payments work?</p>
                <p>You set APR, term, and down payment defaults. We display an estimated “From $X/mo” for each package. Use your own lender or cash pricing—no finance integration required.</p>
              </div>
              <div>
                <p className="font-semibold">Can I add my local utility rebates?</p>
                <p>Yes. Add as many rebates as you need (flat $ or %) and include notes/disclaimer text. We summarize estimated savings on the quote.</p>
              </div>
              <div>
                <p className="font-semibold">Do you support AHRI documentation?</p>
                <p>Yes. Store your AHRI reference and link to the certificate; we display SEER2/HSPF2/AFUE and model numbers per package.</p>
              </div>
              <div>
                <p className="font-semibold">What about adders like crane or long line sets?</p>
                <p>Use Scope & Allowances to add quantity‑based items with min/max/defaults, tier availability, and Better/Best pre‑selects.</p>
              </div>
            </div>
            <div className="text-center mt-12">
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'hvac-faq', action: 'signup' })}>
                <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg mr-4">
                  Start Free — 3 Quotes/Mo
                </Button>
              </Link>
              <Link to="/demo-quote/hvac" onClick={() => analytics.track('Demo Clicked', { location: 'hvac-faq', industry: 'hvac' })}>
                <Button className="px-8 py-4 text-xl font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg" variant="outline">
                  See HVAC Quote Demo →
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Legacy example section hidden (replaced by value props above) */}
        {/* HVAC Quote Example */}
        <section className="hidden">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                See How HVAC Customers Choose Higher-Value Options
              </h2>
              <p className="font-jetbrains text-lg text-gray-600">
                Instead of a single flat rate, customers see three clear options and naturally gravitate toward better value
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto">
              {/* Basic Package */}
              <Card className="relative border border-black transition-all">
                <CardHeader>
                  <div className="flex justify-between items-center mb-2">
                    <CardTitle className="font-instrument text-xl">Standard System</CardTitle>
                    <span className="text-2xl font-bold">$4,500</span>
                  </div>
                  <CardDescription className="font-jetbrains">Reliable HVAC system installation</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Basic 14 SEER unit</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Standard installation</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Basic thermostat</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />1-year parts warranty</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Popular Package */}
              <Card className="relative border border-black transform scale-105">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-black text-white px-4 py-1 rounded-full text-sm font-semibold">Most Popular</span>
                </div>
                <CardHeader>
                  <div className="flex justify-between items-center mb-2">
                    <CardTitle className="font-instrument text-xl">Energy Efficient System</CardTitle>
                    <span className="text-2xl font-bold">$6,500</span>
                  </div>
                  <CardDescription className="font-jetbrains">High-efficiency system with smart features</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />16 SEER high-efficiency unit</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Professional installation</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Programmable smart thermostat</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Enhanced air filtration</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />3-year extended warranty</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Premium Package */}
              <Card className="relative border border-black">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-black text-white px-4 py-1 rounded-full text-sm font-semibold">Best Value</span>
                </div>
                <CardHeader>
                  <div className="flex justify-between items-center mb-2">
                    <CardTitle className="font-instrument text-xl">Premium Smart System</CardTitle>
                    <span className="text-2xl font-bold">$8,500</span>
                  </div>
                  <CardDescription className="font-jetbrains">Top-tier smart HVAC with zone control</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />18+ SEER premium unit</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Complete system upgrade</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Advanced smart thermostat with app</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Multi-zone climate control</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Premium air purification system</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />5-year comprehensive warranty</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Annual maintenance plan</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-12">
              <Link to="/demo-quote/hvac" onClick={() => analytics.track('Demo Clicked', { location: 'hvac-pricing', industry: 'hvac' })}>
                <Button className="px-8 py-4 text-lg font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Try This Interactive HVAC Quote →
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Why HVAC Contractors Choose QuoteTier */}
        <section className="py-16 md:py-24">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                Why HVAC Contractors Choose QuoteTier
              </h2>
              <p className="font-jetbrains text-lg text-gray-600">
                The only quoting software built specifically for service businesses that want to increase job value
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              <Card className="border border-black">
                <CardHeader>
                  <div className="border border-black bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4">
                    <ZapIcon className="h-6 w-6" />
                  </div>
                  <CardTitle className="font-instrument text-xl">Pre-Built HVAC Templates</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground font-jetbrains text-sm">Ready-to-use Good/Better/Best HVAC packages (14 SEER → 16 SEER → 18+ SEER) with all the options your customers want.</p>
                </CardContent>
              </Card>

              <Card className="border border-black">
                <CardHeader>
                  <div className="border border-black bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4">
                    <BarChartIcon className="h-6 w-6" />
                  </div>
                  <CardTitle className="font-instrument text-xl">Real-Time Quote Tracking</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground font-jetbrains text-sm">Know the instant a customer opens your quote, how long they spend reviewing it, and which package they're considering.</p>
                </CardContent>
              </Card>

              <Card className="border border-black">
                <CardHeader>
                  <div className="border border-black bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4">
                    <CheckIcon className="h-6 w-6" />
                  </div>
                  <CardTitle className="font-instrument text-xl">Instant Digital Signatures</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground font-jetbrains text-sm">Close deals immediately with legally-binding digital signatures. No more chasing paperwork or lost contracts.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-5xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                QuoteTier vs Traditional HVAC Quotes
              </h2>
              <p className="font-jetbrains text-lg text-gray-600">
                See why smart HVAC contractors are switching from PDF quotes to interactive quotes
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-6 px-6 font-instrument text-lg font-semibold text-gray-900 bg-gray-50">
                      Feature
                    </th>
                    <th className="text-center py-6 px-6 bg-black text-white">
                      <div className="flex flex-col items-center gap-2">
                        <span className="font-instrument text-lg font-bold">QuoteTier</span>
                        <span className="bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full">
                          HVAC Optimized
                        </span>
                      </div>
                    </th>
                    <th className="text-center py-6 px-6 font-instrument text-lg font-semibold text-gray-700 bg-gray-50">
                      PDF Quotes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Customer can choose package level</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Automatic upselling to higher SEER units</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Know when customer opens quote</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Mobile-friendly for customers</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Time to create quote</td>
                    <td className="py-4 px-6 text-center">
                      <span className="font-jetbrains font-semibold text-gray-800 bg-gray-100 px-3 py-1 rounded-full text-sm">
                        5 minutes
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="font-jetbrains text-gray-600 text-sm">30+ minutes</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-gray-900 to-black text-white">
          <div className="container max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-instrument text-4xl md:text-5xl font-bold mb-6">
              Ready to Increase Your HVAC Job Sizes?
            </h2>
            <p className="font-jetbrains text-xl text-gray-200 mb-8">
              Join hundreds of HVAC contractors using QuoteTier to automatically upsell customers to higher-value packages.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'hvac-final-cta', action: 'signup' })}>
                <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Start Free — 3 Quotes/Mo
                </Button>
              </Link>
              <Link to="/demo-quote/hvac" onClick={() => analytics.track('Demo Clicked', { location: 'hvac-final-cta', industry: 'hvac' })}>
                <Button className="px-8 py-4 text-lg font-semibold border-2 border-white text-white hover:bg-white/10 rounded-lg" variant="outline">
                  Try Interactive Demo First →
                </Button>
              </Link>
            </div>
            
            <p className="font-jetbrains text-sm text-gray-300">
              Free forever • No credit card required • Setup in minutes
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t">
        <div className="container max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-muted-foreground">&copy; 2025 QuoteTier. All rights reserved. <span className="footer-credit">Site by <a href="https://novanorthstudio.com/?utm_source=quotetier.com&utm_medium=referral&utm_campaign=site-credit">Nova North Studio</a></span></p>
          <div className="flex gap-4 mt-4 md:mt-0">
            <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy</Link>
            <a href="mailto:info@quotetier.com" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HVACQuotingSoftware;
